import { Link, usePage } from '@inertiajs/react'
import Navbar from '@/components/layout/Navbar'
import { CheckCircle, Lock, Play } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { SharedProps } from '@/types'

interface SeriesData {
  id: number; title: string; slug: string
  description: string | null; episodes_count: number
}

interface EpItem {
  id: number; title: string; slug: string; position: number | null
  duration: string; is_free: boolean; difficulty: string
  tags: { name: string; slug: string }[]
  completed: boolean; progress_percentage: number
}

interface Props {
  series: SeriesData
  episodes: EpItem[]
  series_progress: number
}

const DIFF_BG: Record<string, string> = {
  beginner: 'bg-green-100 text-green-700',
  intermediate: 'bg-orange-100 text-orange-700',
  advanced: 'bg-red-100 text-red-700',
}

export default function Show({ series, episodes, series_progress }: Props) {
  const { auth } = usePage<SharedProps>().props

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="max-w-3xl mx-auto px-4 py-10">
        {/* Header */}
        <div className="mb-8">
          <Link href="/series" className="text-sm text-gray-400 hover:text-gray-600 mb-3 block">← All series</Link>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">{series.title}</h1>
          <p className="text-gray-500 mb-4">{series.description}</p>
          <div className="flex items-center gap-3 text-sm text-gray-400">
            <span>{series.episodes_count} episodes</span>
            {auth.user && (
              <>
                <span>·</span>
                <span>{series_progress}% complete</span>
              </>
            )}
          </div>
          {auth.user && (
            <div className="mt-3 w-full bg-gray-200 rounded-full h-2">
              <div className="bg-brand h-2 rounded-full transition-all" style={{ width: `${series_progress}%` }} />
            </div>
          )}
        </div>

        {/* Episode list */}
        <div className="space-y-2">
          {episodes.map((ep, idx) => (
            <Link
              key={ep.id}
              href={`/episodes/${ep.slug}`}
              className={cn(
                'flex items-center gap-4 bg-white rounded-xl border px-5 py-4 hover:border-brand/40 hover:shadow-sm transition-all',
                ep.completed ? 'border-green-100' : 'border-gray-100'
              )}
            >
              {/* Position / status icon */}
              <div className="w-8 shrink-0 text-center">
                {ep.completed ? (
                  <CheckCircle className="w-5 h-5 text-brand mx-auto" />
                ) : (
                  <span className="text-sm font-mono text-gray-300">{String(idx + 1).padStart(2, '0')}</span>
                )}
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <p className="font-medium text-gray-900 text-sm truncate">{ep.title}</p>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className={cn('text-xs font-medium px-1.5 py-0.5 rounded-full capitalize', DIFF_BG[ep.difficulty])}>
                    {ep.difficulty}
                  </span>
                  <span className="text-xs text-gray-400">{ep.duration}</span>
                  {ep.tags.slice(0, 2).map(t => (
                    <span key={t.slug} className="text-xs text-gray-400">· {t.name}</span>
                  ))}
                </div>
              </div>

              {/* Access */}
              <div className="shrink-0">
                {ep.is_free ? (
                  <Play className="w-4 h-4 text-gray-300" />
                ) : (
                  auth.user?.subscription_status === 'pro'
                    ? <Play className="w-4 h-4 text-brand" />
                    : <Lock className="w-4 h-4 text-gray-300" />
                )}
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  )
}
